import React, { useRef, useState, useEffect } from "react";
import { useReactToPrint } from "react-to-print";
import { useAccount } from "@/features/workspace/hooks/useAccount";

interface PDFExportProps {
  TriggerComponent: React.FC<{
    onClick: (e: React.MouseEvent) => void;
    loading?: boolean;
  }>;
  PDFComponent: React.ReactNode;
  landscape?: boolean;
  documentTitle?: string;
}

export function PDFExportTrigger(props: PDFExportProps) {
  const {
    TriggerComponent,
    PDFComponent,
    landscape = false,
    documentTitle = "Document",
  } = props;

  const contentRef = useRef<HTMLDivElement>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const { account } = useAccount();

  // When the custom config is not set, we use the account name (if it has a logo) or "Assessio AI"
  const footerTitle =
    account?.config?.pdfFooterTitle || account?.config?.logo
      ? account.name
      : "Assessio AI";

  const logoUrl =
    account?.config?.logo ||
    import.meta.env.VITE_APP_DEFAULT_LOGO ||
    "/src/images/logo/default/assessio.svg";

  const handlePrint = useReactToPrint({
    contentRef,
    documentTitle,
    onBeforePrint: async () => {
      // Set page orientation
      document.documentElement.style.setProperty(
        "--pdf-orientation",
        landscape ? "A4 landscape" : "A4"
      );

      // Add print-specific styles
      const style = document.createElement("style");
      style.setAttribute("data-print-styles", "true");

      style.innerHTML = `
        .pdf-print-header {
          display: block;
          width: 100%;
          padding: 0 0 8mm 0;
          margin: 0 0 8mm 0;
        }
        
        .pdf-print-header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
        }

        @media print {
          @page {
            size: ${landscape ? "A4 landscape" : "A4"};
            margin-top: 20mm;
            margin-bottom: 20mm;
            margin-left: 8mm;
            margin-right: 8mm;
            
            @top-left {
              content: counter(page);
              font-family: proxima-nova;
              font-size: 10px;
              color: #666;
              padding: 8mm 4mm 0 4mm;
            }
            
            @top-right {
              content: "${documentTitle}";
              font-family: proxima-nova;
              font-size: 10px;
              color: #666;
              padding-top: 8mm;
            }
            
            @bottom-center {
              content: "${footerTitle}";
              font-size: 10px;
              color: #666;
            }
          }
          
          @page :first {
            @top-left {
              content: none;
            }
            
            @top-right {
              content: none;
            }
          }

          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .pdf-print-header {
            display: block;
            width: 100%;
            padding: 0mm 4mm 5mm 4mm;
            margin: 0 0 5mm 0;
          }
          
          .pdf-print-header-content {
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
          }

          .pdf-print-header-logo {
            max-height: 36px;
            max-width: 120px;
            object-fit: contain;
          }

          .pdf-print-header-title {
            font-family: proxima-nova;
            font-size: 10px;
            color: #666;
          }
          
          .pdf-page-no-shadow * {
            box-shadow: none !important;
            text-shadow: none !important;
          }
          
          .break-before-page {
            break-before: page;
          }
          
          .break-after-page {
            break-after: page;
          }
          
          .break-inside-avoid {
            break-inside: avoid;
          }

          /* Dynamic content flow styles */
          .pdf-content-section {
            break-inside: avoid;
            margin-bottom: 24px;
          }

          .pdf-new-page-section {
            break-before: page;
            break-inside: avoid;
            margin-bottom: 24px;
          }

          .pdf-flowing-content {
            margin-bottom: 16px;
          }

          /* Ensure proper spacing for dynamic content */
          .dynamic-pdf-document {
            min-height: 100vh;
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
          }
          
          /* Ensure all PDF content respects page boundaries */
          .pdf-content-section,
          .pdf-new-page-section,
          .pdf-flowing-content {
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
          }

          /* Universal PDF content constraints */
          .pdf-content-section,
          .pdf-new-page-section,
          .pdf-flowing-content,
          .dynamic-pdf-document {
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
            overflow: hidden !important;
          }
          
          /* Ensure all child elements respect boundaries */
          .pdf-content-section *,
          .pdf-new-page-section *,
          .pdf-flowing-content *,
          .dynamic-pdf-document * {
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
        }
      `;
      document.head.appendChild(style);
    },
    onAfterPrint: () => {
      setIsPrinting(false);
      // Clean up any added styles
      const printStyles = document.querySelector("style[data-print-styles]");
      if (printStyles) {
        printStyles.remove();
      }
    },
    pageStyle: `
      @page {
        size: ${landscape ? "A4 landscape" : "A4"};
        margin: 20mm 8mm 8mm 8mm;
      }

      @media print {
        body {
          max-width: 100%;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      }
    `,
  });

  const handleOnClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsPrinting(true);

      setTimeout(() => {
        handlePrint();
      }, 1000);
    },
    [handlePrint]
  );

  return (
    <>
      <TriggerComponent onClick={handleOnClick} loading={isPrinting} />
      <div className="fixed -left-full -top-full w-0 h-0 overflow-hidden">
        <div ref={contentRef} className="print:bg-white print:text-grey-900">
          {isPrinting && (
            <>
              <div className="pdf-print-header">
                <div className="pdf-print-header-content">
                  <div className="pdf-print-header-title">{documentTitle}</div>
                  <img
                    src={logoUrl}
                    alt="Logo"
                    className="pdf-print-header-logo"
                  />
                </div>
              </div>
              {PDFComponent}
            </>
          )}
        </div>
      </div>
    </>
  );
}
